// Runs a SAS program through the wasm evaluator (kolistat.run) and
// streams the resulting log envelope into the output panel. Tables
// stay JS-side in DuckDB-wasm — the evaluator emits text tables via
// the legacy formatter, so the panel mirrors what the native CLI
// would print. Errors don't stop execution; the evaluator logs and
// continues, matching native behaviour.

import type { Kolilang } from './wasm';
import { resetSession } from './duckdb';

export interface OutputPanel {
  clear(): void;
  log(text: string): void;
  error(text: string): void;
  /// Structured table render. Unused by the run path today (eval
  /// produces text tables); kept on the interface so a future "show
  /// dataset" affordance can reuse it.
  table(columns: string[], rows: unknown[][]): void;
}

const NOTE_RE = /^\s*NOTE:/i;
const WARN_RE = /^\s*WARNING:/i;
const ERR_RE = /^\s*ERROR:/i;

// Stable marker for the paid-proc entitlement gate. The engine emits
// "ERROR: PROC <NAME> is not available yet. Create a free account…" and
// only this gate uses that phrase, so a substring match is enough to
// distinguish it from any other ERROR line. A structured ABI flag would
// be sturdier but is an ABI change — out of scope here.
const PAID_PROC_GATE_RE = /is not available yet/;

function logClass(text: string): string {
  if (ERR_RE.test(text)) return 'out-err';
  if (WARN_RE.test(text)) return 'out-warn';
  if (NOTE_RE.test(text)) return 'out-note';
  return 'out-log';
}

export function attachOutputPanel(host: HTMLElement): OutputPanel {
  const scrollToEnd = (): void => { host.scrollTop = host.scrollHeight; };
  const appendLine = (cls: string, text: string): void => {
    const pre = document.createElement('pre');
    pre.className = cls;
    pre.textContent = text;
    host.appendChild(pre);
    scrollToEnd();
  };
  return {
    clear: () => { host.innerHTML = ''; },
    log: (text) => appendLine(logClass(text), text),
    error: (text) => appendLine('out-err', text),
    table: (columns, rows) => {
      const table = document.createElement('table');
      table.className = 'out-table';
      const thead = document.createElement('thead');
      const hr = document.createElement('tr');
      for (const c of columns) {
        const th = document.createElement('th');
        th.textContent = c;
        hr.appendChild(th);
      }
      thead.appendChild(hr);
      table.appendChild(thead);
      const tbody = document.createElement('tbody');
      for (const row of rows) {
        const tr = document.createElement('tr');
        for (const cell of row) {
          const td = document.createElement('td');
          td.textContent = cell == null ? '.' : String(cell);
          tr.appendChild(td);
        }
        tbody.appendChild(tr);
      }
      table.appendChild(tbody);
      host.appendChild(table);
      scrollToEnd();
    },
  };
}

/// Drop the in-browser DuckDB session — used by the toolbar's reset
/// button and by the tutorial mode between lessons. Re-establishing
/// the connection happens lazily on the next kolistat.run call.
export async function resetRunSession(): Promise<void> {
  await resetSession();
}

export async function runProgram(
  kolistat: Kolilang,
  source: string,
  out: OutputPanel,
  // Fired once per run when the engine gated a paid proc (see
  // PAID_PROC_GATE_RE). main.ts owns the response (signup CTA); the
  // runner stays UI-agnostic. The engine's own message still renders in
  // the panel via emitLines — the callback is purely the extra nudge.
  onPaidProcBlocked?: () => void,
): Promise<void> {
  out.clear();
  let result;
  try {
    result = await kolistat.run(source);
  } catch (e) {
    out.error(`runner failed: ${e instanceof Error ? e.message : String(e)}`);
    return;
  }
  for (const d of result.diagnostics) {
    out.error(`ERROR (${d.s}-${d.e}): ${d.msg}`);
  }
  emitLines(result.out, out, false);
  emitLines(result.err, out, true);
  if (onPaidProcBlocked && PAID_PROC_GATE_RE.test(result.err)) {
    onPaidProcBlocked();
  }
}

function emitLines(text: string, out: OutputPanel, asError: boolean): void {
  if (!text) return;
  // Eval streams already-formatted lines (the LegacyFormatter
  // terminates with '\n'). Split and drop the trailing empty so we
  // don't render a phantom blank line, but preserve internal blanks
  // — PROC PRINT separators rely on them.
  const lines = text.split('\n');
  if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
  for (const line of lines) {
    if (asError) out.error(line);
    else out.log(line);
  }
}
